import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Search, Tags, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function InventoryAliases({ onOpenItem }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyWithout, setOnlyWithout] = useState(false);
  const [adding, setAdding] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (onlyWithout) params.set("only_without", "true");
      const { data: d } = await api.get(`/inventory/aliases?${params}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [search, onlyWithout]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const run = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      if (message) toast.success(message);
      setAdding("");
      setDraft("");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const tryLookup = async () => {
    if (!search.trim()) return;
    try {
      const { data: r } = await api.get(`/inventory/resolve?q=${encodeURIComponent(search)}`);
      setLookup(r);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="inventory-aliases">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" data-testid="alias-search-input"
            placeholder='Try what staff type — "1 inch pipe", "flap disc"'
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button size="sm" variant="outline" data-testid="alias-lookup-button" onClick={tryLookup}>
          Test this search
        </Button>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="alias-only-without-toggle" checked={onlyWithout}
            onCheckedChange={setOnlyWithout} />
          Only items with no other name
        </label>
        <span className="text-[11px] text-muted-foreground" data-testid="alias-counts">
          {data.total_aliases} other name(s) • {data.items_without_alias} item(s) still have none
        </span>
      </div>

      {lookup && (
        <div className="surface p-3 text-xs" data-testid="alias-lookup-result">
          {lookup.found ? (
            <>
              “{lookup.query}” finds{" "}
              <button type="button" className="font-medium hover:underline"
                onClick={() => onOpenItem?.(lookup.item_id)}>
                {lookup.official_name}
              </button>{" "}
              <span className="text-muted-foreground">
                (matched on “{lookup.matched_on}” • {lookup.quantity} {lookup.unit} in{" "}
                {lookup.warehouse_name})
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">
              “{lookup.query}” does not match any item yet — add it as another name below.
            </span>
          )}
          <button type="button" className="ml-2 text-muted-foreground hover:underline"
            onClick={() => setLookup(null)}>clear</button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Other names are search names only. Every name leads to the same item, the same stock
        balance and the same history — a name can never create a second stock record.
      </p>

      <div className="space-y-2">
        {data.items.map((it) => (
          <div key={it.item_id} className="surface p-3" data-testid={`alias-row-${it.item_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <button type="button" className="text-sm font-medium hover:underline"
                  data-testid={`alias-item-name-${it.item_id}`}
                  onClick={() => onOpenItem?.(it.item_id)}>
                  {it.official_name}
                </button>
                <div className="text-[11px] text-muted-foreground">
                  {[it.category_name, it.subcategory].filter(Boolean).join(" → ") || "no category"}
                  {" • "}{it.quantity} {it.unit} • {it.warehouse_name}
                </div>
              </div>
              {data.can_manage && (
                adding === it.item_id ? (
                  <div className="flex items-center gap-1">
                    <Input autoFocus className="h-8 w-56" data-testid="alias-new-input"
                      placeholder='e.g. 1" MS pipe' value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && run(
                        () => api.post(`/inventory/items/${it.item_id}/aliases`, { alias: draft }),
                        "Other name added")} />
                    <Button size="icon" className="h-8 w-8" disabled={busy}
                      data-testid="alias-new-save"
                      onClick={() => run(
                        () => api.post(`/inventory/items/${it.item_id}/aliases`, { alias: draft }),
                        "Other name added")}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8"
                      onClick={() => { setAdding(""); setDraft(""); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                    data-testid={`alias-add-button-${it.item_id}`}
                    onClick={() => { setAdding(it.item_id); setDraft(""); }}>
                    <Plus className="h-3 w-3" /> Add other name
                  </Button>
                )
              )}
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {it.aliases.length === 0 && (
                <span className="text-[11px] text-muted-foreground">
                  No other names yet{data.can_manage ? " — add what your staff actually say." : "."}
                </span>
              )}
              {it.aliases.map((a) => (
                editing?.item === it.item_id && editing?.alias === a ? (
                  <span key={a} className="flex items-center gap-1">
                    <Input autoFocus className="h-7 w-48" data-testid="alias-edit-input"
                      value={draft} onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && run(
                        () => api.put(
                          `/inventory/items/${it.item_id}/aliases/${encodeURIComponent(a)}`,
                          { new_alias: draft }), "Other name updated")} />
                    <Button size="icon" className="h-7 w-7" data-testid="alias-edit-save"
                      disabled={busy}
                      onClick={() => run(() => api.put(
                        `/inventory/items/${it.item_id}/aliases/${encodeURIComponent(a)}`,
                        { new_alias: draft }), "Other name updated")}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => setEditing(null)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                ) : (
                  <Badge key={a} variant="secondary" className="gap-1.5 py-1 text-[11px]"
                    data-testid={`alias-chip-${it.item_id}-${a}`}>
                    <Tags className="h-3 w-3" /> {a}
                    {data.can_manage && (
                      <>
                        <button type="button" title="Rename"
                          data-testid={`alias-edit-${it.item_id}-${a}`}
                          onClick={() => { setEditing({ item: it.item_id, alias: a }); setDraft(a); }}>
                          <Pencil className="h-2.5 w-2.5" />
                        </button>
                        <button type="button" title="Remove"
                          data-testid={`alias-delete-${it.item_id}-${a}`}
                          onClick={() => run(() => api.delete(
                            `/inventory/items/${it.item_id}/aliases/${encodeURIComponent(a)}`),
                            "Other name removed")}>
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </>
                    )}
                  </Badge>
                )
              ))}
            </div>
          </div>
        ))}
        {data.items.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="alias-empty">
            No item matches that search.
          </p>
        )}
      </div>
    </div>
  );
}
